// markdownHelpers.js
import { default as unified } from 'unified';
import remarkParse from 'remark-parse';
import remark2rehype from 'remark-rehype';
import rehypeSanitize from 'rehype-sanitize';
import rehypeReact from 'rehype-react';
import React, { createElement, useState, Children, cloneElement, useEffect, useRef } from 'react';
import '../styles/markdownStyles.css';

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

const textFromNode = node => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  const t = node.type;
  if (t === 'text') return node.value || '';
  if (t === 'html') return String(node.value || '').replace(/<[^>]*>/g, '');
  if (node.children && node.children.length) {
    return node.children.map(textFromNode).join('');
  }
  return '';
};

export const markdownToPlainText = markdownContent => {
  if (!markdownContent) return '';
  try {
    const tree = unified().use(remarkParse).parse(markdownContent);
    const parts = (tree.children || []).map(textFromNode).filter(Boolean);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  } catch (error) {
    return String(markdownContent || '').replace(/\s+/g, ' ').trim();
  }
};

const stripPrefixFromFirstText = (node, re) => {
  if (!node) return false;
  if (typeof node === 'string') return false;
  if (node.type === 'text') {
    const v = node.value || '';
    const nv = v.replace(re, '');
    if (nv !== v) {
      node.value = nv;
      return true;
    }
    return false;
  }
  if (node.children && node.children.length) {
    for (const ch of node.children) {
      if (stripPrefixFromFirstText(ch, re)) return true;
    }
  }
  return false;
};

function remarkCallouts() {
  const SRC_RES = [
    { lang: 'en', re: /^\s*source\s*:/iu },
    { lang: 'el', re: /^\s*πηγή\s*:/iu },
    { lang: 'el', re: /^\s*απ[όο]\s+(?:την?|τον?|το|τους|τις|του|της)?\s*/iu },
    { lang: 'de', re: /^\s*quelle\s*:/iu },
  ];

  return tree => {
    const top = tree.children || [];
    for (const node of top) {
      if (node.type !== 'paragraph') continue;
      const txt = textFromNode(node).trim();

      let matched = null;
      for (const entry of SRC_RES) {
        if (entry.re.test(txt)) {
          matched = entry;
          break;
        }
      }
      if (matched) {
        node.data = node.data || {};
        node.data.hName = 'div';
        node.data.hProperties = {
          className: ['callout', 'callout--source'],
          role: 'note',
          'aria-label': { en: 'Source', el: 'Πηγή', de: 'Quelle' }[matched.lang],
          'data-callout-lang': matched.lang,
        };
      }
    }
  };
}

function remarkStyledParagraphToH4() {
  const isPureUnderlineHtml = value => {
    const v = String(value || '');
    const stripped = v.replace(/<\/?u>/gi, '');
    return (
      v.replace(/<u>/gi, '').replace(/<\/u>/gi, '') === stripped && !/<(?!\/?u\b)[^>]*>/.test(v)
    );
  };

  const isStyledInline = n => {
    if (!n) return false;
    if (n.type === 'text') return n.value.trim().length === 0;
    if (n.type === 'strong' || n.type === 'emphasis') {
      return (n.children || []).every(isStyledInline);
    }
    if (n.type === 'html') {
      return isPureUnderlineHtml(n.value);
    }
    if (n.children && n.children.length) {
      return (n.children || []).every(isStyledInline);
    }
    return false;
  };

  return tree => {
    const top = tree.children || [];
    for (let i = 0; i < top.length; i++) {
      const node = top[i];
      if (node.type !== 'paragraph') continue;

      const kids = node.children || [];
      if (!kids.length) continue;

      const allStyled = kids.every(isStyledInline);
      if (!allStyled) continue;

      top[i] = {
        type: 'heading',
        depth: 4,
        children: kids,
      };
    }
  };
}

function remarkBulletParasToOl() {
  const BULLET_PREFIX = /^\s*[•\u2022\u00B7\-–—]\s*/;

  return tree => {
    const children = tree.children || [];
    const out = [];
    let buffer = [];

    const flush = () => {
      if (!buffer.length) return;
      out.push({
        type: 'list',
        ordered: true,
        spread: false,
        children: buffer.map(p => ({
          type: 'listItem',
          spread: false,
          checked: null,
          children: [{ type: 'paragraph', children: p.children }],
        })),
      });
      buffer = [];
    };

    for (const node of children) {
      if (node.type === 'paragraph') {
        const totalText = textFromNode(node);
        if (/^\s*[•\u2022\u00B7\-–—]\s*\S/.test(totalText)) {
          stripPrefixFromFirstText(node, BULLET_PREFIX);
          buffer.push(node);
          continue;
        }
      }
      flush();
      out.push(node);
    }
    flush();

    tree.children = out;
  };
}

function remarkSoftBreaks() {
  const splitTextNode = node => {
    if (!node || node.type !== 'text' || typeof node.value !== 'string') {
      return [node];
    }

    if (!node.value.includes('\n')) {
      return [node];
    }

    const parts = node.value.split('\n');
    const output = [];

    parts.forEach((part, index) => {
      if (part.length > 0) {
        output.push({ ...node, value: part });
      }

      if (index < parts.length - 1) {
        output.push({ type: 'break' });
      }
    });

    return output.length > 0 ? output : [{ ...node, value: '' }];
  };

  const visit = node => {
    if (!node || typeof node !== 'object' || !Array.isArray(node.children)) {
      return;
    }

    node.children = node.children.flatMap(child => {
      visit(child);
      return splitTextNode(child);
    });
  };

  return tree => visit(tree);
}

function rehypeHeadingClasses() {
  return tree => {
    const visit = node => {
      if (!node || typeof node !== 'object') return;
      const { type, tagName } = node;
      if (type === 'element' && /^h[1-6]$/.test(tagName)) {
        const level = Number(tagName.slice(1));
        const props = (node.properties = node.properties || {});
        const existing = props.className || [];
        const cls = Array.isArray(existing) ? existing : [existing].filter(Boolean);
        props.className = Array.from(new Set([...cls, 'md-h', `md-h${level}`]));
      }
      if (node.children) for (const ch of node.children) visit(ch);
    };
    visit(tree);
  };
}

const TAP_MOVE_THRESHOLD_PX = 8;
const TAP_TIME_THRESHOLD_MS = 300;

const ZoomableImage = ({ src, alt, title, ...props }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, moved: false, t0: 0 });
  const overlayRef = useRef(null);

  const open = () => setIsZoomed(true);
  const close = () => setIsZoomed(false);
  const toggle = () => setIsZoomed(z => !z);

  useEffect(() => {
    if (!isZoomed) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const swallowOutside = e => {
      const overlay = overlayRef.current;
      const insideOverlay = overlay && overlay.contains(e.target);
      if (!insideOverlay) {
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', swallowOutside, true);
    document.addEventListener('touchend', swallowOutside, true);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('click', swallowOutside, true);
      document.removeEventListener('touchend', swallowOutside, true);
    };
  }, [isZoomed]);

  const onTouchStart = e => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, moved: false, t0: Date.now() };
  };

  const onTouchMove = e => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - touchRef.current.x);
    const dy = Math.abs(t.clientY - touchRef.current.y);
    if (dx > TAP_MOVE_THRESHOLD_PX || dy > TAP_MOVE_THRESHOLD_PX) {
      touchRef.current.moved = true;
    }
  };

  const onTouchEnd = e => {
    const dt = Date.now() - touchRef.current.t0;
    if (!touchRef.current.moved && dt <= TAP_TIME_THRESHOLD_MS) {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      toggle();
    }
  };

  const onClose = e => {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
    close();
  };

  const onOverlayClick = e => {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
    close();
  };
  const stop = e => {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className={`zoomable-image-container ${isZoomed ? 'zoomed' : ''}`}>
      <img
        src={src}
        alt={alt}
        title={title}
        className="zoomable-image"
        role="button"
        aria-label={isZoomed ? `Close zoomed image: ${alt}` : `Zoom image: ${alt}`}
        onClick={open}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        {...props}
      />

      {isZoomed && (
        <div
          ref={overlayRef}
          className="zoom-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image overlay"
          onClick={onOverlayClick}
          onTouchEnd={onOverlayClick}
        >
          <img
            src={src}
            alt={alt}
            title={title}
            className="zoomed-image"
            onClick={stop}
            onTouchStart={stop}
            onTouchMove={stop}
            onTouchEnd={stop}
          />
          <button
            className="zoom-close-button"
            type="button"
            aria-label="Close zoomed image"
            onClick={onClose}
            onTouchEnd={onClose}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

const defaultSchema = {
  tagNames: [
    'b',
    'i',
    'u',
    'em',
    'strong',
    'a',
    'p',
    'br',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'pre',
    'hr',
    'span',
    'img',
    'div',
  ],
  attributes: {
    a: ['href', 'name', 'target', 'rel'],
    img: ['src', 'alt', 'title'],
    '*': ['className', 'style', 'role', 'aria-label', 'data-callout-lang'],
  },
};
const customSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes.a || []), 'href', 'target', 'rel'],
    img: [...(defaultSchema.attributes.img || []), 'src', 'alt', 'title'],
  },
};

const fixBrokenLinks = markdown =>
  (markdown || '').replace(/\[([^\]]+)\]\((?:link)\)/g, (_m, text) => {
    const url = /^https?:\/\//i.test(text) ? text : `https://${text}`;
    return `[${text}](${url})`;
  });

const SourceCallout = ({ lang = 'en', children }) => {
  const TITLES = { en: 'Source:', el: 'Πηγή:', de: 'Quelle:' };

  const PREFIX_RE = /^\s*(?:source|πηγή|quelle)\s*:\s*|^\s*απ[όο]\s+(?:την?|τον?|το|τους|τις|του|της)?\s*/iu;

  const flattenChildren = nodes =>
    Children.toArray(nodes).flatMap(child => {
      if (React.isValidElement(child) && child.props?.children) {
        return flattenChildren(child.props.children);
      }
      return [child];
    });

  const stripPrefix = nodes => {
    const arr = [...nodes];
    if (!arr.length) return arr;
    const first = arr[0];
    if (typeof first === 'string') {
      const nv = first.replace(PREFIX_RE, '');
      if (nv !== first) arr[0] = nv;
    }
    return arr;
  };

  const removeEmphasis = nodes =>
    Children.toArray(nodes).map((n, i) => {
      if (!React.isValidElement(n)) return n;
      if (n.type === 'em' || n.type === 'i') {
        return (
          <React.Fragment key={`flat-${i}`}>{removeEmphasis(n.props.children)}</React.Fragment>
        );
      }
      return cloneElement(n, n.props, removeEmphasis(n.props.children));
    });

  const flattened = flattenChildren(children);
  const cleaned = stripPrefix(removeEmphasis(flattened));

  return (
    <div
      className="source-box"
      role="note"
      aria-label={TITLES[lang]}
      style={{
        background: '#FBF7F0',
        borderRadius: '12px',
        padding: '14px 16px',
        display: isMobile ? 'block' : 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        flexWrap: 'wrap',
        lineHeight: 1.4,
      }}
    >
      <span
        className="source-title"
        style={{
          color: '#7D5559',
          fontWeight: 600,
          fontSize: '14px',
          whiteSpace: 'nowrap',
        }}
      >
        {TITLES[lang]}
      </span>

      <span
        className="source-content"
        style={{
          color: '#7D5559',
          fontStyle: 'normal',
          fontSize: '14px',
          fontWeight: 400,
          marginLeft: '2px',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          letterSpacing: '0.01em',
        }}
      >
        {cleaned}
      </span>

      <style>{`
        .source-break-mobile { display: none; }
        @media (max-width: 767px) {
          .source-break-mobile { display: block; width: 100%; height: 0; }
        }
      `}</style>
    </div>
  );
};

// ✅ Updated: supports EN "Option", DE "Option"/"Auswahl", EL "Επιλογή"/"Επιλογη"
const OPTION_SECTION_HEADING_RE = /^\s*(?:option|auswahl|επιλογ[ήη])\s+\d+([:.\-–—]\s+|\s+)/iu;

function remarkWrapOptionSections() {
  return tree => {
    const children = tree.children || [];
    const out = [];
    let currentWrapper = null;

    const pushWrapper = () => {
      if (currentWrapper) {
        out.push(currentWrapper);
        currentWrapper = null;
      }
    };

    for (const node of children) {
      const txt = textFromNode(node).trim();
      const isOptionHeading =
        (node.type === 'heading' || node.type === 'paragraph') &&
        OPTION_SECTION_HEADING_RE.test(txt);

      if (isOptionHeading) {
        pushWrapper();
        currentWrapper = {
          type: 'optionSection',
          data: {
            hName: 'div',
            hProperties: { className: ['md-option-card'] },
          },
          children: [node],
        };
        continue;
      }

      if (currentWrapper) {
        currentWrapper.children.push(node);
      } else {
        out.push(node);
      }
    }
    pushWrapper();
    tree.children = out;
  };
}

export const hasOptionSections = (markdownContent = '') =>
  OPTION_SECTION_HEADING_RE.test(markdownContent || '');

export const convertMarkdownToReact = (markdownContent, shouldSplitOrOptions = false) => {
  const options =
    typeof shouldSplitOrOptions === 'object' && shouldSplitOrOptions !== null
      ? shouldSplitOrOptions
      : { shouldSplit: shouldSplitOrOptions };
  const { shouldSplit = false, preserveSoftBreaks = false } = options;
  const fixedMarkdown = fixBrokenLinks(markdownContent || '');
  const splittedMarkdown = fixedMarkdown
    .split(' ')
    .slice(0, 10)
    .join(' ');
  const truncatedContent = shouldSplit ? `${splittedMarkdown}...` : fixedMarkdown;

  const result = unified()
    .use(remarkParse)
    .use(() => tree => {
      if (preserveSoftBreaks) {
        return remarkSoftBreaks()(tree);
      }
      return tree;
    })
    .use(remarkCallouts)
    .use(remarkBulletParasToOl)
    .use(remarkStyledParagraphToH4)
    .use(remarkWrapOptionSections)
    .use(remark2rehype)
    .use(rehypeHeadingClasses)
    .use(rehypeSanitize, customSanitizeSchema)
    .use(rehypeReact, {
      createElement,
      components: {
        a: ({ href, children, ...props }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        ),
        img: ZoomableImage,
        div: props => {
          const cls = props.className || '';
          const list = Array.isArray(cls)
            ? cls
            : String(cls)
                .split(' ')
                .filter(Boolean);

          if (list.includes('callout--source')) {
            const lang = props['data-callout-lang'] || 'en';
            return <SourceCallout lang={lang}>{props.children}</SourceCallout>;
          }
          return <div className="practiceStep" {...props} />;
        },
      },
    })
    .processSync(truncatedContent);

  return result.result;
};
